import { clearSaved } from './saved';
import { clearWatchMeta } from './watch';

/**
 * Подключение обработчика работы без связи (public/sw.js, МП-02). Вызывается при открытии площадки:
 * сначала обработчик ставится, затем ему передаются уже загруженные файлы портала, чтобы он открывался в режиме полёта.
 * В режиме разработки (vite dev) обработчик не ставится: он мешал бы горячей перезагрузке.
 */
export async function registerOfflineShell(): Promise<'ready' | 'unsupported' | 'dev' | 'failed'> {
  if (!('serviceWorker' in navigator)) return 'unsupported';
  if (!import.meta.env.PROD) return 'dev';
  try {
    await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    const registration = await navigator.serviceWorker.ready;
    const urls = performance
      .getEntriesByType('resource')
      .map((entry) => new URL(entry.name, location.href))
      .filter((url) => url.origin === location.origin && url.pathname.startsWith('/assets/'))
      .map((url) => url.pathname);
    registration.active?.postMessage({ type: 'precache', urls });
    // Обработчик начинает отвечать странице не сразу после установки: ждём, пока он возьмёт страницу под управление
    if (!navigator.serviceWorker.controller)
      await new Promise<void>((resolve) => {
        const done = () => resolve();
        navigator.serviceWorker.addEventListener('controllerchange', done, { once: true });
        setTimeout(done, 4000);
      });
    // Кто вошёл и список проектов запоминаются сразу: без них площадка без связи не откроется
    await Promise.allSettled([
      fetch('/api/v1/auth/me', { credentials: 'same-origin', headers: { accept: 'application/json' } }),
      fetch('/api/v1/projects', { credentials: 'same-origin', headers: { accept: 'application/json' } }),
    ]);
    return 'ready';
  } catch {
    return 'failed';
  }
}

/** Стереть запомненное для работы без связи (выход, смена человека на телефоне). */
export async function wipeOfflineData(): Promise<void> {
  clearWatchMeta();
  clearSaved();
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration('/');
  registration?.active?.postMessage('wipe');
}
